// React-query hooks for the adaptive-sampling feature. Every UI module should
// consume these — never call the endpoints module directly from a component.

import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationOptions,
  type UseQueryOptions,
} from "@tanstack/react-query";
import { useEffect, useRef } from "react";

import type {
  CampaignConfig,
  CampaignState,
  ControlAction,
  OracleDataset,
  SamplePoint,
  ScoreResponse,
  SurfaceResponse,
  WebSocketEvent,
} from "../types";
import {
  createCampaign,
  fetchCampaignState,
  fetchScore,
  fetchSurface,
  listCampaigns,
  listOracles,
  nextBatch,
  postControl,
  postFork,
  postManualSample,
  postObservations,
  type CampaignSummary,
  type CreateCampaignResponse,
  type ForkRequest,
  type ObservationItem,
  type ScoreParams,
  type SurfaceParams,
} from "./endpoints";
import { CampaignStream } from "./websocket";

// ---- Query keys ------------------------------------------------------------

export const adaptiveSamplingKeys = {
  root: ["adaptive-sampling"] as const,
  oracles: () => [...adaptiveSamplingKeys.root, "oracles"] as const,
  campaignList: () => [...adaptiveSamplingKeys.root, "campaigns"] as const,
  campaign: (id: string) =>
    [...adaptiveSamplingKeys.root, "campaign", id] as const,
  surface: (id: string, params: SurfaceParams) =>
    [...adaptiveSamplingKeys.root, "campaign", id, "surface", params] as const,
  score: (id: string, params: ScoreParams) =>
    [...adaptiveSamplingKeys.root, "campaign", id, "score", params] as const,
};

// ---- Queries ---------------------------------------------------------------

export function useOraclesQuery(
  options?: Omit<
    UseQueryOptions<{ oracles: OracleDataset[] }, Error, OracleDataset[]>,
    "queryKey" | "queryFn" | "select"
  >,
) {
  return useQuery({
    queryKey: adaptiveSamplingKeys.oracles(),
    queryFn: listOracles,
    select: (data) => data.oracles,
    staleTime: 5 * 60 * 1000,
    ...options,
  });
}

export function useCampaignListQuery(
  options?: Omit<
    UseQueryOptions<
      { campaigns: CampaignSummary[] },
      Error,
      CampaignSummary[]
    >,
    "queryKey" | "queryFn" | "select"
  >,
) {
  return useQuery({
    queryKey: adaptiveSamplingKeys.campaignList(),
    queryFn: listCampaigns,
    select: (data) => data.campaigns,
    // Poll at 3 Hz — cheap in mock, and the Dashboard list should reflect
    // live campaign status (e.g. queued → running → done).
    refetchInterval: 2_000,
    staleTime: 1_500,
    ...options,
  });
}

export function useCampaignStateQuery(
  id: string | undefined,
  options?: Omit<
    UseQueryOptions<CampaignState>,
    "queryKey" | "queryFn" | "enabled"
  >,
) {
  return useQuery({
    queryKey: adaptiveSamplingKeys.campaign(id ?? ""),
    queryFn: () => fetchCampaignState(id!),
    enabled: !!id,
    ...options,
  });
}

export function useSurfaceQuery(
  id: string | undefined,
  params: SurfaceParams,
  options?: Omit<
    UseQueryOptions<SurfaceResponse>,
    "queryKey" | "queryFn" | "enabled"
  >,
) {
  return useQuery({
    queryKey: adaptiveSamplingKeys.surface(id ?? "", params),
    queryFn: () => fetchSurface(id!, params),
    enabled: !!id && !!params.output,
    ...options,
  });
}

export function useScoreQuery(
  id: string | undefined,
  params: ScoreParams,
  options?: Omit<
    UseQueryOptions<ScoreResponse>,
    "queryKey" | "queryFn" | "enabled"
  >,
) {
  return useQuery({
    queryKey: adaptiveSamplingKeys.score(id ?? "", params),
    queryFn: () => fetchScore(id!, params),
    enabled: !!id,
    ...options,
  });
}

// ---- Mutations -------------------------------------------------------------

export function useCreateCampaignMutation(
  options?: UseMutationOptions<CreateCampaignResponse, Error, CampaignConfig>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (config: CampaignConfig) => createCampaign(config),
    onSuccess: (data, ...rest) => {
      qc.setQueryData(adaptiveSamplingKeys.campaign(data.id), data.state);
      options?.onSuccess?.(data, ...rest);
    },
    ...options,
  });
}

export function useManualSampleMutation(
  id: string,
  options?: UseMutationOptions<
    { point: SamplePoint },
    Error,
    Record<string, number>
  >,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (inputs: Record<string, number>) => postManualSample(id, inputs),
    onSuccess: (_data, ...rest) => {
      qc.invalidateQueries({ queryKey: adaptiveSamplingKeys.campaign(id) });
      options?.onSuccess?.(_data, ...rest);
    },
    ...options,
  });
}

export function useObservationsMutation(
  id: string,
  options?: UseMutationOptions<
    { state: CampaignState },
    Error,
    ObservationItem[]
  >,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (points: ObservationItem[]) => postObservations(id, points),
    onSuccess: (data, ...rest) => {
      qc.setQueryData(adaptiveSamplingKeys.campaign(id), data.state);
      options?.onSuccess?.(data, ...rest);
    },
    ...options,
  });
}

export function useNextBatchMutation(
  id: string,
  options?: UseMutationOptions<{ proposedPoints: SamplePoint[] }, Error, void>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => nextBatch(id),
    onSuccess: (data, ...rest) => {
      qc.invalidateQueries({ queryKey: adaptiveSamplingKeys.campaign(id) });
      options?.onSuccess?.(data, ...rest);
    },
    ...options,
  });
}

export function useControlMutation(
  id: string,
  options?: UseMutationOptions<{ state: CampaignState }, Error, ControlAction>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (action: ControlAction) => postControl(id, action),
    onSuccess: (data, ...rest) => {
      qc.setQueryData(adaptiveSamplingKeys.campaign(id), data.state);
      options?.onSuccess?.(data, ...rest);
    },
    ...options,
  });
}

export function useForkMutation(
  id: string,
  options?: UseMutationOptions<CreateCampaignResponse, Error, ForkRequest>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (req: ForkRequest) => postFork(id, req),
    onSuccess: (data, ...rest) => {
      qc.setQueryData(adaptiveSamplingKeys.campaign(data.id), data.state);
      options?.onSuccess?.(data, ...rest);
    },
    ...options,
  });
}

// ---- Stream hook -----------------------------------------------------------

/**
 * Subscribe to a campaign's WebSocket stream and invalidate the corresponding
 * react-query cache on each state/point/batch/metrics event. Optional onEvent
 * callback receives every frame.
 */
export function useCampaignStream(
  id: string | undefined,
  onEvent?: (event: WebSocketEvent) => void,
): void {
  const qc = useQueryClient();
  const handlerRef = useRef(onEvent);
  handlerRef.current = onEvent;

  useEffect(() => {
    if (!id) return;
    const stream = new CampaignStream();
    stream.connect(id, (event) => {
      try {
        handlerRef.current?.(event);
      } catch {
        // Ignore handler-thrown errors.
      }
      switch (event.type) {
        case "state":
          qc.setQueryData(adaptiveSamplingKeys.campaign(id), event.state);
          break;
        case "point":
        case "batch":
        case "metrics":
          qc.invalidateQueries({ queryKey: adaptiveSamplingKeys.campaign(id) });
          break;
        case "completed":
        case "error":
          qc.invalidateQueries({ queryKey: adaptiveSamplingKeys.campaign(id) });
          break;
      }
    });
    return () => {
      stream.disconnect();
    };
  }, [id, qc]);
}
